import React, { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Skeleton from "@mui/material/Skeleton";
import { useAppTheme } from "@/utils/theme";
import { UserFormData } from "./validation.schema";

export interface GroupedRole {
  title: string;
  roles: {
    _id: string;
    name: string;
    type: string;
  }[];
}

interface TabRolesProps {
  rolesArray: GroupedRole[];
  isSubmitting?: boolean;
  clearSubmitting?: () => void;
  isLoadingRoles?: boolean;
}

export const TabRoles: FC<TabRolesProps> = ({
  rolesArray,
  isLoadingRoles = false,
}) => {
  const theme = useAppTheme();
  const {
    control,
    formState: { errors },
  } = useFormContext<UserFormData>();

  // AC00-D: Loading skeletons
  if (isLoadingRoles) {
    return (
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={40} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={40} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={40} />
        </Box>
      </Box>
    );
  }

  // Find groups by title
  const standardSystemRolesGroup = rolesArray.find(
    (g) => g.title.toLowerCase() === "standard system roles",
  );
  const standardAccountRolesGroup = rolesArray.find(
    (g) => g.title.toLowerCase() === "standard account roles",
  );
  const customRolesGroup = rolesArray.find(
    (g) => g.title.toLowerCase() === "custom roles",
  );

  const hasNoRoles =
    !standardSystemRolesGroup &&
    !standardAccountRolesGroup &&
    !customRolesGroup;

  // AC01: Calculate grid sizes based on which groups are present
  const getGridSize = (group: GroupedRole | undefined) => {
    if (!group) return { xs: 0, sm: 0 };

    const hasSystem = !!standardSystemRolesGroup;
    const hasAccount = !!standardAccountRolesGroup;
    const hasCustom = !!customRolesGroup;

    const totalGroups = [hasSystem, hasAccount, hasCustom].filter(
      Boolean,
    ).length;

    if (totalGroups === 1) return { xs: 12, sm: 12 };
    if (totalGroups === 2) return { xs: 12, sm: 6 };
    return { xs: 12, sm: 4 }; // 3 groups
  };

  return (
    <Box>
      {hasNoRoles ? (
        <Typography variant="bodyMedium" sx={{ textAlign: "center", py: 4 }}>
          No roles available
        </Typography>
      ) : (
        <Controller
          name="roleId"
          control={control}
          defaultValue={""}
          render={({ field }) => (
            <RadioGroup
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
              sx={{ width: "100%" }}
            >
              <Grid container spacing={3}>
                {/* Standard System Roles Column - First position per design images */}
                {standardSystemRolesGroup && (
                  <Grid size={getGridSize(standardSystemRolesGroup)}>
                    <Typography
                      variant="titleMedium"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {standardSystemRolesGroup.title}
                    </Typography>
                    {standardSystemRolesGroup.roles.map((role) => (
                      <FormControlLabel
                        key={role._id}
                        value={role._id}
                        control={<Radio />}
                        label={role.name}
                        sx={{ display: "block" }}
                      />
                    ))}
                  </Grid>
                )}

                {/* Standard Account Roles Column */}
                {standardAccountRolesGroup && (
                  <Grid size={getGridSize(standardAccountRolesGroup)}>
                    <Typography
                      variant="titleMedium"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {standardAccountRolesGroup.title}
                    </Typography>
                    {standardAccountRolesGroup.roles.map((role) => (
                      <FormControlLabel
                        key={role._id}
                        value={role._id}
                        control={<Radio />}
                        label={role.name}
                        sx={{ display: "block" }}
                      />
                    ))}
                  </Grid>
                )}

                {/* Custom Roles Column - AC01: Only show if there are custom roles */}
                {customRolesGroup && (
                  <Grid size={getGridSize(customRolesGroup)}>
                    <Typography
                      variant="titleMedium"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {customRolesGroup.title}
                    </Typography>
                    {customRolesGroup.roles.map((role) => (
                      <FormControlLabel
                        key={role._id}
                        value={role._id}
                        control={<Radio />}
                        label={role.name}
                        sx={{ display: "block" }}
                      />
                    ))}
                  </Grid>
                )}
              </Grid>
            </RadioGroup>
          )}
        />
      )}

      {/* AC05: Show validation error message */}
      {errors.roleId && (
        <Typography variant="bodySmall" sx={{ color: "error.main", mt: 2 }}>
          {errors.roleId.message || "You must select one role for this user."}
        </Typography>
      )}
    </Box>
  );
};
